import { CancelButton } from '@Components/Buttons';
import SensorDataUploader from '@Components/SensorDataUploader/SensorDataUploader';
import {
	error as notificationError,
	PageWrapper,
	success,
	useSettings,
} from '@innovyze/stylovyze';
import { StyledControlsWrapper } from './UploadSensorData.styles';
import { useGlobalization } from '@Translations';
import {
	TimeSeriesUploadElement,
	TimeSeriesUploadWithFile,
	UploadFileStatusType,
} from '@Types/sensor.types';
import { addParam } from '@Utils/actions';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import LoadingButton from './ActionButton';
import { SpinnerStatusIndicator } from '@Pages/EditSensor/SpinnerStatusIndicator';
import { refreshBulkUploadSessionId } from '@Actions';
import { getSensorTimeSeriesUploadURL } from '@Services/sensors';
import { getValue } from '@Selectors';
import { AxiosResponse } from 'axios';

export default function UploadSensorData() {
	const { t } = useGlobalization();
	const history = useHistory();
	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(refreshBulkUploadSessionId());
	}, []);

	const [submitButtonDisabled, setSubmitButtonDisabled] =
		React.useState(true);
	const [filesTimeZone, setFilesTimeZone] = React.useState('UTC');
	const [uploadFiles, setUploadFiles] = React.useState<
		Array<TimeSeriesUploadWithFile>
	>([]);
	const [spinning, setSpinning] = React.useState(false);
	const [spinnerText, setSpinnerText] = React.useState('initial');
	const [uploadStatus, setUploadStatus] =
		React.useState<UploadFileStatusType>('initial');

	React.useEffect(() => {
		if (uploadFiles.length > 0) setSubmitButtonDisabled(false);
	}, [uploadFiles]);

	const goToSensors = () => {
		history.push(
			addParam({
				name: 'tab',
				value: '4',
				tab: 'sensor-configuration',
			}),
		);
	};
	const handleError = () => {
		dispatch(
			notificationError(
				t('Error uploading sensor data, try again later'),
			),
		);
	};

	const uploadFile = async (timeSeriesFile: TimeSeriesUploadWithFile) => {
		const { status } = await fetch(timeSeriesFile.url, {
			method: 'PUT',
			body: timeSeriesFile.file,
		});
		// NOTE: Chrome erroneously logs "Fetch Failed" when the response body is empty, even if the request was successful
		if (status !== 200) {
			throw new Error('Error uploading sensor data, try again later');
		}
	};

	const { timeZoneIANA } = useSettings().companySettings;
	const { sessionId } = useSelector(getValue).bulkUpload;

	const getS3SignedURLs = async function (
		tsFiles: TimeSeriesUploadWithFile[],
	): Promise<Array<TimeSeriesUploadWithFile>> {
		try {
			const preparedUploadsResponse: AxiosResponse<
				TimeSeriesUploadElement[]
			> = await getSensorTimeSeriesUploadURL(
				tsFiles.map(file => file.name),
				timeZoneIANA,
				sessionId,
				filesTimeZone,
			);
			if (preparedUploadsResponse.status === 200) {
				return preparedUploadsResponse.data.map((fileMeta, index) => {
					// WARNING: This assumes that the response index matches the original order
					return {
						...fileMeta,
						file: tsFiles[index],
					} as unknown as TimeSeriesUploadWithFile;
				});
			} else {
				setUploadStatus('error');
			}
		} catch (error) {
			setUploadStatus('error');
		}
		return [];
	};

	const handleUploadButtonClick = async () => {
		if (uploadFiles.length <= 0) {
			setSpinning(false);
			return;
		}
		try {
			setSpinnerText('Preparing');
			setUploadStatus('preparing');
			setSpinning(true);
			setSubmitButtonDisabled(true);
			const s3urls = await getS3SignedURLs(uploadFiles);

			setSpinnerText('Uploading');
			setUploadStatus('uploading');
			const promises = [];
			for (const f of s3urls) {
				promises.push(uploadFile(f));
			}
			await Promise.all(promises);
			dispatch(success(t('Sensor data uploaded successfully')));
			goToSensors();
		} catch (e) {
			setUploadStatus('error');
			handleError();
		} finally {
			setSpinning(false);
		}
	};

	const secondaryButtons = (
		<StyledControlsWrapper>
			<SpinnerStatusIndicator text={spinnerText} visible={spinning} />
			<CancelButton
				key="UploadSensorDataCancel"
				data-cy="cancel"
				onClick={goToSensors}
				style={{ textTransform: 'none' }}>
				{t('Cancel')}
			</CancelButton>
			<LoadingButton
				key="UploadSensorDataSubmit"
				data-cy="uploadData"
				color="primary"
				disabled={submitButtonDisabled}
				loading={false}
				onClick={handleUploadButtonClick}
				style={{ textTransform: 'none' }}>
				{t('Upload Data')}
			</LoadingButton>
		</StyledControlsWrapper>
	);

	return (
		<PageWrapper
			isLoading={false}
			title={t('Upload Sensor Data')}
			secondary={secondaryButtons}
			headerUnderline={true}
			applyPadding={true}>
			<SensorDataUploader
				uploadFiles={uploadFiles}
				setUploadFiles={setUploadFiles}
				setFilesTimeZone={setFilesTimeZone}
				setSubmitButtonDisabled={setSubmitButtonDisabled}
				setSpinning={setSpinning}
				setSpinnerText={setSpinnerText}
				uploadStatus={uploadStatus}
				setUploadStatus={setUploadStatus}
			/>
		</PageWrapper>
	);
}
